import { useEffect, useState, lazy, Suspense } from "react";
import { Switch, Route, Router as WouterRouter, useLocation, useSearch } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { useInactivityLogout } from "@/hooks/use-inactivity-logout";
import { verifyGateToken } from "@/lib/accessGate";
import { Layout } from "@/components/layout";
import { BetaEndedScreen } from "@/components/beta-ended-screen";
import { CookieConsentBanner } from "@/components/cookie-consent-banner";
import { ChunkErrorBoundary } from "@/components/chunk-error-boundary";
import { clearChunkReloadFlag } from "@/lib/chunkReload";
import { WebInstallHost, isWebInstallPath } from "@/web/WebInstall";
import { AuthSeamBridge } from "@/web/authSeam";

// Route components are lazy-loaded so each page ships as its own chunk and the
// initial bundle only contains the app shell + the landing page.
const Landing = lazy(() => import("@/pages/landing").then((m) => ({ default: m.Landing })));
const Help = lazy(() => import("@/pages/help").then((m) => ({ default: m.Help })));
const Contact = lazy(() => import("@/pages/contact").then((m) => ({ default: m.Contact })));
const Feedback = lazy(() => import("@/pages/feedback").then((m) => ({ default: m.Feedback })));
const About = lazy(() => import("@/pages/about").then((m) => ({ default: m.About })));
const Admin = lazy(() => import("@/pages/admin").then((m) => ({ default: m.Admin })));
const Beta = lazy(() => import("@/pages/beta").then((m) => ({ default: m.Beta })));
const BetaFeedback = lazy(() => import("@/pages/beta-feedback").then((m) => ({ default: m.BetaFeedback })));
const GuideDetail = lazy(() => import("@/pages/guides").then((m) => ({ default: m.GuideDetail })));
/* /login and /register REDIRECT into the marketplace's own day-mode auth
   (Blake 2026-08-31): one auth UI, and a direct link never lands on the
   support-shell pages again. The page COMPONENTS stay in the tree untouched —
   their tests import them directly, and the auth logic they exercise
   (AuthContext.signIn/signUp + the register sequence) is exactly what the
   pop-up calls through the __gbzAuth seam (src/web/authSeam.tsx). replace(),
   not assign(): the old URL should not survive in history to bounce a
   back-button press through the redirect again. */
const Login = () => {
  useEffect(() => window.location.replace("/#/auth/signin"), []);
  return null;
};
const Register = () => {
  useEffect(() => window.location.replace("/#/auth/register"), []);
  return null;
};
const ForgotPassword = lazy(() => import("@/pages/forgot-password").then((m) => ({ default: m.ForgotPassword })));
/* /profile REDIRECTS into the web app's own profile (Blake 2026-09-14), the
   same move /login and /register above already make and for the same reason:
   one profile UI, and a bookmark or an in-code navigation never lands on the
   old support-shell account page again.

   The page COMPONENT stays in the tree untouched — pages/profile.tsx is still
   imported directly by its own tests, exactly as the Login comment above
   describes for its pages. What changes is only what the ROUTE renders.

   replace(), not assign(): the old URL must not survive in history, or a
   back-button press bounces straight through the redirect again.

   🪤 The old page carried surfaces the new profile does not: support tickets
   and bug reports. /my-tickets still exists on its own and is reachable, which
   is the answer to the first open question on the card — but it is worth
   knowing that this route is the last thing that linked them together. */
const Profile = () => {
  useEffect(() => window.location.replace("/#/profile"), []);
  return null;
};
const CreateListing = lazy(() => import("@/pages/create-listing").then((m) => ({ default: m.CreateListing })));
/* The settings tab on its own route (option 2 for #888, Blake 2026-09-18): the
   web's Delete Account control lands here, because /profile redirects and its
   ?tab=settings never arrives. accountSettingsRoute.test.tsx guards that this
   path is a page, never a redirect. */
const AccountSettings = lazy(() => import("@/pages/account-settings").then((m) => ({ default: m.AccountSettings })));
const Runway = lazy(() => import("@/pages/runway").then((m) => ({ default: m.Runway })));
const BrandGuide = lazy(() => import("@/pages/brand").then((m) => ({ default: m.BrandGuide })));
const Waitlist = lazy(() => import("@/pages/waitlist").then((m) => ({ default: m.Waitlist })));
const Terms = lazy(() => import("@/pages/terms").then((m) => ({ default: m.Terms })));
const Privacy = lazy(() => import("@/pages/privacy").then((m) => ({ default: m.Privacy })));
const Dmca = lazy(() => import("@/pages/dmca").then((m) => ({ default: m.Dmca })));
const RefundPolicy = lazy(() => import("@/pages/refund-policy").then((m) => ({ default: m.RefundPolicy })));
const Accessibility = lazy(() => import("@/pages/accessibility").then((m) => ({ default: m.Accessibility })));
const FlowView = lazy(() => import("@/pages/flow-view").then((m) => ({ default: m.FlowView })));
const NotFound = lazy(() => import("@/pages/not-found"));
const TicketLookup = lazy(() => import("@/pages/ticket-lookup").then((m) => ({ default: m.TicketLookup })));
const MyTickets = lazy(() => import("@/pages/my-tickets").then((m) => ({ default: m.MyTickets })));
const StripeConnectReturn = lazy(() => import("@/pages/stripe-connect-return").then((m) => ({ default: m.StripeConnectReturn })));
const StripeConnectRefresh = lazy(() => import("@/pages/stripe-connect-refresh").then((m) => ({ default: m.StripeConnectRefresh })));
const ListingLanding = lazy(() => import("@/pages/listing-landing").then((m) => ({ default: m.ListingLanding })));
const FreebieLanding = lazy(() => import("@/pages/freebie-landing").then((m) => ({ default: m.FreebieLanding })));

// Web install (WebInstallHost, imported above) is rendered unconditionally
// in App() below, outside the Switch, so it is never mounted/unmounted by
// navigation — its own script/style boot is guarded to run at most once, on
// first arrival at "/" or its /web alias (isWebInstallPath). Eagerly
// imported, not lazy: it's small, and it needs to exist before any route
// (including the very first paint) so its #app node is already there the one
// time app.js's render() fires. Router() below just steps aside (renders
// null) on these paths so no other chrome mounts alongside it.

// The announcement banner is the only consumer of framer-motion in the eager app
// shell. Lazy-loading it keeps motion-vendor out of the first-paint preload set;
// the banner is non-critical and only renders when an announcement exists.
const AnnouncementBanner = lazy(() =>
  import("@/components/announcement-banner").then((m) => ({ default: m.AnnouncementBanner })),
);

const queryClient = new QueryClient();

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );
}

// Scroll to top on every route change unless there's a hash (to allow deep-links).
function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if (window.location.hash) return;
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location]);
  return null;
}

function AdminGuard() {
  const { user, isAdmin, loading } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
    } else if (!isAdmin) {
      /* Straight to the web app rather than through the /profile redirect: a
         wouter navigate() here would render the redirect component, which then
         does its own location.replace — two hops and a frame of blank page for
         a destination we already know. */
      window.location.replace("/#/profile");
    }
  }, [user, isAdmin, loading, navigate]);

  useEffect(() => {
    if (loading) document.title = "GOBAZAAR Support";
  }, [loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user || !isAdmin) return null;
  return <Admin />;
}


// Investor gate: a server-signed, expiring token unlocks /runway. The token is
// minted by an admin (POST /api/gates/investor-link) and validated server-side
// (POST /api/gates/verify) — no access secret ships in the client bundle.
const INVESTOR_SESSION_KEY = "runway_investor_key";

function RunwayGuard() {
  const { user, isAdmin, loading } = useAuth();
  const [, navigate] = useLocation();
  const search = useSearch();
  const [investorOk, setInvestorOk] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setChecking(true);
      const params = new URLSearchParams(search);
      const key = params.get("key") ?? "";
      const stored = sessionStorage.getItem(INVESTOR_SESSION_KEY) ?? "";
      const token = key || stored;
      const ok = token ? await verifyGateToken("investor", token) : false;
      if (cancelled) return;
      if (ok) {
        sessionStorage.setItem(INVESTOR_SESSION_KEY, token);
        setInvestorOk(true);
      } else {
        sessionStorage.removeItem(INVESTOR_SESSION_KEY);
        setInvestorOk(false);
      }
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [search]);

  useEffect(() => {
    if (investorOk || checking || loading) return;
    if (!user) navigate("/login");
    else if (!isAdmin) window.location.replace("/#/profile");
  }, [investorOk, checking, user, isAdmin, loading, navigate]);

  useEffect(() => {
    if (!investorOk && loading) document.title = "GOBAZAAR Support";
  }, [investorOk, loading]);

  if (!investorOk && (checking || loading)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!investorOk && (!user || !isAdmin)) return null;
  return <Runway isAdmin={!investorOk && isAdmin} />;
}

function InactivityGuard() {
  const { user, signOut } = useAuth();
  const isAuthenticated = !!user;
  useInactivityLogout(isAuthenticated, signOut);
  return null;
}

// Send a page_view to Google Analytics on every Wouter client-side navigation.
// GA (gtag.js) is loaded only after cookie consent (see src/lib/consent.ts), so
// this no-ops until the visitor accepts; the gtag check below guards that. The
// page view for the acceptance page itself is emitted from the consent banner's
// accept handler, since this effect won't re-run without a route change.
function AnalyticsTracker() {
  const [location] = useLocation();

  useEffect(() => {
    const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag !== "function") return;
    gtag("event", "page_view", {
      page_path: location,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location]);

  return null;
}

// Brand gate: same server-signed token model as the investor gate (scope "brand").
const BRAND_SESSION_KEY = "brand_access_key";

function BrandGuard() {
  const { user, isAdmin, loading } = useAuth();
  const [, navigate] = useLocation();
  const search = useSearch();
  const [brandOk, setBrandOk] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setChecking(true);
      const params = new URLSearchParams(search);
      const key = params.get("key") ?? "";
      const stored = sessionStorage.getItem(BRAND_SESSION_KEY) ?? "";
      const token = key || stored;
      const ok = token ? await verifyGateToken("brand", token) : false;
      if (cancelled) return;
      if (ok) {
        sessionStorage.setItem(BRAND_SESSION_KEY, token);
        setBrandOk(true);
      } else {
        sessionStorage.removeItem(BRAND_SESSION_KEY);
        setBrandOk(false);
      }
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [search]);

  useEffect(() => {
    if (brandOk || checking || loading) return;
    if (!user) navigate("/login");
    else if (!isAdmin) window.location.replace("/#/profile");
  }, [brandOk, checking, user, isAdmin, loading, navigate]);

  useEffect(() => {
    if (!brandOk && loading) document.title = "GOBAZAAR Support";
  }, [brandOk, loading]);

  if (!brandOk && (checking || loading)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!brandOk && (!user || !isAdmin)) return null;
  return <BrandGuide isAdmin={!brandOk && isAdmin} />;
}

function AdminRoutes() {
  return (
    <ThemeProvider forceMode="day" surface="ops">
      <Layout>
        <ChunkErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Switch>
              <Route path="/admin" component={AdminGuard} />
              <Route path="/admin/flows/:id/view">
                {(params) => <FlowView id={params.id ?? ""} />}
              </Route>
            </Switch>
          </Suspense>
        </ChunkErrorBoundary>
      </Layout>
    </ThemeProvider>
  );
}

function Router() {
  const [location] = useLocation();
  const { betaEnded, betaEndedMessage } = useAuth();
  const isAdmin = location === "/admin" || location.startsWith("/admin/");
  // "/" used to select Landing here. Owner instruction 2026-08-23: the
  // installed demo (WebInstallHost, mounted unconditionally below) moves to
  // "/" as the site's real homepage; /web stays as a working alias. Because
  // this branch is checked BEFORE isLanding, isLanding can now never be true
  // — Landing (and its lazy import above) is left in place, unreachable
  // through this router, rather than deleted: nothing else in the app
  // references it, but removing a still-importable page isn't what "mount
  // the demo at /" asked for, and it's a one-line revert if that changes.
  const isLanding = location === "/";
  const isWebInstall = isWebInstallPath(location);

  // Forced, no-escape beta-ended interstitial. Admins are never flagged (the API
  // returns 200 for them), so this can never lock an admin out. The /beta-feedback
  // route stays reachable so the only available action — sharing feedback — works.
  if (betaEnded && location !== "/beta-feedback") {
    return <BetaEndedScreen message={betaEndedMessage} />;
  }

  if (isAdmin) {
    // Admin branch: AdminRoutes already wraps itself in <ThemeProvider forceMode="day" surface="ops">.
    // No outer ThemeProvider is active here — only one provider writes data-mode at a time.
    return (
      <>
        <InactivityGuard />
        <AdminRoutes />
      </>
    );
  }

  // Web install route — Blake's demo owns the whole document here, same as
  // Landing does. The actual host (WebInstallHost) is mounted unconditionally
  // in App(), outside this Switch, so it survives navigation in/out/in
  // without re-injecting anything; this branch steps aside so no other chrome
  // (Layout/nav/footer/ThemeProvider) mounts alongside it.
  //
  // ThemeProvider specifically MUST NOT mount here, and that got harder to
  // reason about in v47, not easier. v44's Blake render() wrote
  // document.documentElement.dataset.mode = store.mode on every repaint
  // (app.js:3704), which kept <html> in sync with the clock with no
  // ThemeProvider active. v47 deleted that line — his new render() writes
  // app.dataset.mode onto #app instead (a child of the demo's OWN
  // `.blake-install-scope` wrapper, never <html>), so nothing on "/" drives
  // <html data-mode> any more and it would otherwise stick at index.html's
  // hardcoded "night" 24/7. (Since card 1219711747205363,
  // public/first-paint-mode.js sets it once before first paint by the web's
  // rule; nothing on "/" changes it after load.) The tempting fix is mounting <ThemeProvider>
  // here — DON'T: it writes data-mode onto <html>, which the web app does not
  // read (it themes its own scope root), and it would put a second theme
  // owner on the site's highest-traffic route. (Until 2026-09-14 it also asked
  // for geolocation and called ip-api.com; useDayNightMode now uses the web's
  // clock rule and makes no network call.) The actual fix stays inside Blake's own
  // scope instead: WebInstallHost's existing "mirror mode continuously"
  // effect (below) now reads #app's data-mode (which v47's render() sets,
  // synchronously, BEFORE app.innerHTML — app.js:3723) and copies it onto the
  // scope root, so :scope[data-mode="night"] still tracks store.mode's clock
  // exactly as it always did, with zero new components and zero new network
  // calls. See WebInstall.tsx's own comment on that effect, and
  // src/__tests__/webInstallThemeMode.test.tsx for the reproduction and fix.
  //
  // EXCEPT these two, which are not chrome — they are site-wide controls, and
  // this branch used to `return null` before both of the branches that mount
  // them (recon 23, L3). "/" is the site's most-visited route, so the effect
  // was that first-time visitors landing on the homepage were never offered
  // the cookie/session disclosure at all — and because Google Analytics only
  // loads on stored consent (lib/consent.ts), a root-only session could not
  // even be measured. A signed-in tab parked on "/" likewise never armed the
  // 30-minute inactivity auto-logout; navigating away then called
  // resetActivity() on mount, restarting the clock rather than expiring it.
  //
  // Both render null or a fixed-position overlay and neither pulls in Layout,
  // nav, footer or ThemeProvider. The banner sits OUTSIDE the demo's scope
  // root (WebInstallHost's own <div class="blake-install-scope">, a sibling of
  // this subtree), and styles.scoped.css is entirely wrapped in
  // `@scope (.blake-install-scope)`, so none of Blake's rules can reach it and
  // it cannot repaint his styling; its own colors come from index.css's
  // palette for <html>'s mode, which public/first-paint-mode.js sets before
  // first paint and nothing on "/" changes after load: no ThemeProvider needed.
  if (isWebInstall) {
    return (
      <>
        <InactivityGuard />
        <CookieConsentBanner />
      </>
    );
  }

  // Landing page has its own nav/footer — render it outside <Layout>. DEAD
  // as of the "/" mount move above: isWebInstall now matches "/" and returns
  // first, so isLanding can never be true. Left in place, not deleted — see
  // the isLanding declaration above.
  if (isLanding) {
    return (
      <ThemeProvider>
        <ScrollToTop />
        <InactivityGuard />
        <ChunkErrorBoundary>
          <Suspense fallback={null}>
            <Landing />
          </Suspense>
        </ChunkErrorBoundary>
        <CookieConsentBanner />
      </ThemeProvider>
    );
  }

  return (
    // Public branch: wrap only these routes in the auto day/night ThemeProvider.
    <ThemeProvider>
      <ScrollToTop />
      <InactivityGuard />
      <ChunkErrorBoundary>
        <Suspense fallback={null}>
          <AnnouncementBanner />
        </Suspense>
      </ChunkErrorBoundary>
      <Layout>
      <ChunkErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
      <Switch>
        <Route path="/help" component={Help} />
        <Route path="/help/:slug">
          {(params) => <GuideDetail slug={params.slug ?? ""} />}
        </Route>
        <Route path="/contact" component={Contact} />
        <Route path="/feedback" component={Feedback} />
        <Route path="/tickets/lookup" component={TicketLookup} />
        <Route path="/my-tickets" component={MyTickets} />
        <Route path="/about" component={About} />
        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/profile" component={Profile} />
        <Route path="/account/settings" component={AccountSettings} />
        <Route path="/create-listing" component={CreateListing} />
        <Route path="/runway" component={RunwayGuard} />
        <Route path="/brand" component={BrandGuard} />
        <Route path="/beta" component={Beta} />
        <Route path="/beta-feedback" component={BetaFeedback} />
        <Route path="/stripe-connect-return" component={StripeConnectReturn} />
        <Route path="/stripe-connect-refresh" component={StripeConnectRefresh} />
        <Route path="/waitlist" component={Waitlist} />
        <Route path="/terms" component={Terms} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/dmca" component={Dmca} />
        <Route path="/refund-policy" component={RefundPolicy} />
        <Route path="/accessibility" component={Accessibility} />
        <Route path="/listing/:id" component={ListingLanding} />
        <Route path="/freebie/:id" component={FreebieLanding} />
        <Route component={NotFound} />
      </Switch>
      </Suspense>
      </ChunkErrorBoundary>
    </Layout>
    <CookieConsentBanner />
    </ThemeProvider>
  );
}

function App() {
  // Clear the stale-chunk reload guard only after the app has stayed alive for
  // a few seconds. A genuine stale-chunk reload loop resolves within ~1s (failed
  // import() -> ChunkErrorBoundary catch -> hard reload), well before this fires,
  // so a still-broken bundle keeps the guard set and surfaces the manual-reload
  // fallback instead of looping. Surviving the delay proves the current bundle
  // is healthy, which re-arms recovery for a future deploy in this same tab.
  useEffect(() => {
    const timer = window.setTimeout(clearChunkReloadFlag, 8000);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <AnalyticsTracker />
            {/* Unconditional, outside the Switch — see WebInstall.tsx for why. */}
            <WebInstallHost />
            {/* window.__gbzAuth for the marketplace's auth pop-up — Tyler's
                AuthContext calls, unchanged, behind the seam app.js consumes. */}
            <AuthSeamBridge />
            <Router />
          </WouterRouter>
        </AuthProvider>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
